import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, pluck, share } from 'rxjs/operators';

/** available message types. */
export enum MessageBusType {
	Generic = 'generic',
	LoginState = 'loginState',
	AuthChanges = 'authChanges',
	EPO = 'epo',
	ConnectedApp = 'connectedApp',
	LeaveConnectedApp = 'leaveConnectedApp',
}

/** the format of a message */
export interface Message {
	/** type as defined in above enum */
	type: MessageBusType;
	/** subtype, can be used to diversify in a certain module */
	subType?: string;
	/** payload, usually json-like data */
	payload: any;
}

/**
 * Messagebus service
 * @example
 * ```ts
 /* sample on how to use:
    // const s = new MessageBusService(); // in an angular app you should inject this.
    // do not use it like this, as it will get a separate instance.,

    // (async function() {
    // 	s.ListenForAllMessages().subscribe(r => console.log(r));

    // 	const generics$ = s.observableOfType<string>(MessageType.Generic);
    // 	const typedEm = s.createTypedEmitter<string>(MessageType.Generic);

    // 	generics$.subscribe(r => console.log(r));

    // 	s.emit({ type: MessageType.Generic, payload: 'hello' });
    // 	typedEm('world');
    // })();
 */
@Injectable({
	providedIn: 'root',
})
export class MessageBusService {
	/** subject for pushing out messages, internal */
	private messages = new ReplaySubject<Message>(1);

	/**
	 * Internal observable that publishes the pushed messages.
	 * note that it doesn't replay!
	 */
	private messages$ = this.messages.pipe(share());

	/**
	 * emit sends a message down the bus
	 */
	emit = (m: Message) => {
		this.messages.next(m);
	};

	/**
	 * Util function that returns a function that can emit messages of a set Type.
	 */
	createTypedEmitter = <T>(type: MessageBusType, subType?: string) => (payload: T) => this.emit({ type, payload, subType });

	/**
	 * returns an observable that emits ALL messages that pass. Do only use this for debugging purposes.
	 */
	all = (): Observable<Message> => {
		return this.messages$;
	};

	/**
	 * Returns an typed observable that returns the payload for the event in question.
	 * if an subtype is provided this will also be filtered on. Only the payload is available to subscribers
	 * There is no replay, so you will only receive changes after subscribing, and not any previous payloads!
	 */
	on = <T>(type: MessageBusType, subType?: string): Observable<T> => {
		return this.messages$.pipe(
			filter(m => m.type === type && (subType === undefined || m.subType === subType)),
			pluck('payload')
		);
	};
}
